import { TextIcon } from '@src/components/common/icons';
import { KARAOKE_TRACK_NUMBER } from '@src/stores/VideoCreatorStore';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import CircleIcon from '@src/svgs/CircleIcon';
import FrameLockIcon from '@src/svgs/FrameLockIcon';
import MuteKaraokeIcon from '@src/svgs/MuteKaraokeIcon';
import TimelineAudioIcon from '@src/svgs/TimelineAudioIcon';
import TimelineCompositionIcon from '@src/svgs/TimelineCompositionIcon';
import TimelineLogoIcon from '@src/svgs/TimelineLogoIcon';
import TimelinePhotoIcon from '@src/svgs/TimelinePhotoIcon';
import TimelineShapeIcon from '@src/svgs/TimelineShapeIcon';
import VideoIcon from '@src/svgs/VideoIcon';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import type { ElementState } from '@src/renderer/ElementState';

interface TimelineTrackLabelsProps {
  tracks: [number, ElementState[]][];
  activeTracks: number[];
  offsetY: number;
  getTrackInfo: (
    track: number,
    elements: ElementState[],
  ) => {
    isOriginalVideo: boolean;
    isText: boolean;
    isShape: boolean;
    isComposition: boolean;
    isLogo: boolean;
    isActive: boolean;
    height: number;
  };
}

const TOOLTIP_STYLE = {
  padding: '8px',
  fontSize: '12px',
  lineHeight: '120%',
  zIndex: '10',
  fontWeight: 600,
  color: '#F3E9D7',
  background: '#484848',
};

function getLockFrameMessage(isLocked: boolean): string {
  return isLocked
    ? 'Unlock framing across elements on the track'
    : 'Lock framing across elements on the track';
}

export const TimelineTrackLabels: React.FC<TimelineTrackLabelsProps> = observer(
  ({ tracks, offsetY, getTrackInfo }) => {
    const videoCreator = useVideoCreatorStore();

    const renderTrackContent = (track: number, elements: ElementState[]) => {
      const trackInfo = getTrackInfo(track, elements);
      const { isOriginalVideo, isLogo, isShape, isComposition } = trackInfo;

      if (isOriginalVideo) {
        return (
          <>
            <CircleIcon fillColor="#006FEE" />
            <VideoIcon strokeColor="#F3E9D7" width="14" height="14" />
            <span className="text">Video</span>
            <LockButton
              isLocked={videoCreator.frameLockedTracks.includes(track)}
              onClick={() =>
                videoCreator.trackManager.toggleTrackFrameLock(track)
              }
              data-tooltip-id="framing-lock-tooltip"
              data-tooltip-content={getLockFrameMessage(
                videoCreator.frameLockedTracks.includes(track),
              )}
            >
              <FrameLockIcon fillColor="currentColor" />
              <Tooltip
                id="framing-lock-tooltip"
                style={TOOLTIP_STYLE}
                place="top"
                positionStrategy="fixed"
              />
            </LockButton>
          </>
        );
      } else if (isLogo) {
        return (
          <>
            <CircleIcon fillColor="#EFA65D" />
            <TimelineLogoIcon fill="#F3E9D7" width={14} height={14} />
            <span className="text">Logo</span>
          </>
        );
      } else if (elements.some(e => videoCreator.isImageElement(e))) {
        return (
          <>
            <CircleIcon fillColor="#17C964" />
            <TimelinePhotoIcon strokeColor="#F3E9D7" width="14" height="14" />
            <span className="text">Photo</span>
            <LockButton
              isLocked={videoCreator.frameLockedTracks.includes(track)}
              onClick={() =>
                videoCreator.trackManager.toggleTrackFrameLock(track)
              }
              data-tooltip-id="framing-lock-tooltip"
              data-tooltip-content={getLockFrameMessage(
                videoCreator.frameLockedTracks.includes(track),
              )}
            >
              <FrameLockIcon fillColor="currentColor" />
            </LockButton>
          </>
        );
      } else if (elements[0].source.type === 'video') {
        return (
          <>
            <CircleIcon fillColor="#006FEE" />
            <VideoIcon strokeColor="#F3E9D7" width="14" height="14" />
            <span className="text" style={{ whiteSpace: 'nowrap' }}>
              B-Roll
            </span>
            <LockButton
              isLocked={videoCreator.frameLockedTracks.includes(track)}
              onClick={() =>
                videoCreator.trackManager.toggleTrackFrameLock(track)
              }
              data-tooltip-id="framing-lock-tooltip"
              data-tooltip-content={getLockFrameMessage(
                videoCreator.frameLockedTracks.includes(track),
              )}
            >
              <FrameLockIcon fillColor="currentColor" />
            </LockButton>
          </>
        );
      } else if (elements[0].source.type === 'audio') {
        return (
          <>
            <CircleIcon fillColor="#7828C8" />
            <TimelineAudioIcon strokeColor="#F3E9D7" width="14" height="14" />
            <span className="text">Music</span>
          </>
        );
      } else if (isShape) {
        return (
          <>
            <CircleIcon fillColor="#17C964" />
            <TimelineShapeIcon strokeColor="#F3E9D7" width="14" height="14" />
            <span className="text">Shape</span>
          </>
        );
      } else if (isComposition) {
        return (
          <>
            <CircleIcon fillColor="#17C964" />
            <TimelineCompositionIcon
              strokeColor="#F3E9D7"
              width="14"
              height="14"
            />
            <span className="text">Graphics</span>
          </>
        );
      } else if (elements[0].source.track === KARAOKE_TRACK_NUMBER) {
        return (
          <>
            <CircleIcon fillColor="#CBD7F0" />
            <MuteKaraokeIcon strokeColor="#F3E9D7" width="14" height="14" />
            <span className="text">Karaoke</span>
          </>
        );
      } else {
        return (
          <>
            <CircleIcon fillColor="#CBD7F0" />
            <TextIcon fillColor="#F3E9D7" width="14" height="14" />
            <span className="text">Text</span>
          </>
        );
      }
    };

    return (
      <LeftTrackWrapper>
        <LeftTrackWrapperInner style={{ top: -1 * offsetY }}>
          {tracks.map(([track, elements]) => {
            const trackInfo = getTrackInfo(track, elements);
            const {
              isActive,
              isOriginalVideo,
              isText,
              isShape,
              isComposition,
              isLogo,
            } = trackInfo;

            return (
              <LeftTrack
                key={`track-${track}`}
                linkedVideo={isOriginalVideo}
                isText={isText}
                isShape={isShape}
                isComposition={isComposition}
                isActive={isActive}
                onClick={
                  !isOriginalVideo
                    ? async () => {
                        await videoCreator.setActiveElements();
                        videoCreator.selectedTrack = track;
                      }
                    : undefined
                }
                isLogo={isLogo}
              >
                {renderTrackContent(track, elements)}
              </LeftTrack>
            );
          })}
        </LeftTrackWrapperInner>
      </LeftTrackWrapper>
    );
  },
);

const LeftTrackWrapper = styled.div`
  position: absolute;
  z-index: 2;
  width: 100%;
  height: calc(100% - 35px);
  padding-bottom: 100px;
  box-sizing: border-box;
  overflow: hidden;
`;

const LeftTrackWrapperInner = styled.div`
  position: absolute;
  width: 100%;
`;

const LeftTrack = styled.div<{
  linkedVideo: boolean;
  isText: boolean;
  isActive: boolean;
  isLogo?: boolean;
  isShape: boolean;
  isComposition: boolean;
}>`
  position: relative;
  height: ${props => {
    if (props.linkedVideo) return '99px';
    if (props.isText || props.isLogo || props.isShape || props.isComposition)
      return '38px';
    return '78px';
  }};
  ${props => (props.isActive ? 'background-color: #48484880;' : '')}
  display: flex;
  gap: 8px;
  padding-left: 32px;
  padding-right: 5px;
  -webkit-box-align: center;
  align-items: center;
  cursor: pointer;
  .text {
    color: #f3e9d7;
    font-family: Inter;
    font-size: 10px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }
`;

const LockButton = styled.div<{ isLocked: boolean }>`
  outline: 0;
  background-color: ${props => (props.isLocked ? '#F2D093' : 'transparent')};
  border-radius: 4px;
  color: ${props => (props.isLocked ? '#03041A' : '#F2D093')};
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  padding: 4px;
  margin-left: 8px;
`;
